import { TextField as MuiTextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ChartHelpTooltip from '../common/ChartHelpTooltip';
import { ChangeEvent, Fragment, useEffect, useState } from 'react';
import { useFormContext } from '../../contexts/FormContext';
import { useFormData } from '../../hooks/useFormValues';
import { isValidNumberInput } from '../../utils/utils';
import useDebouncedCallback from '../../hooks/useDebouncedCallback';
import type { FieldComponentProps } from '../form-engine/types';

const NumberField = ({ field }: FieldComponentProps) => {
  const { getFormDataItem, handleChange } = useFormContext();
  const formData = useFormData();
  const formValue = getFormDataItem(formData, field.name!);
  const [inputValue, setInputValue] = useState(formValue);

  const debouncedHandleChange = useDebouncedCallback((value: string) => {
    handleChange(
      {
        target: {
          name: field.name!,
          value,
          type: field.type,
          checked: false,
        },
      },
      field,
    );
  }, 500);

  useEffect(() => {
    setInputValue(formValue);
  }, [formValue]);

  let isHidden = Object.prototype.hasOwnProperty.call(field, 'isHidden') ? getFormDataItem(formData, field.isHidden!) : false;

  if (Object.prototype.hasOwnProperty.call(field, 'revertHidden') && field?.revertHidden) {
    isHidden = !isHidden;
  }

  if (isHidden) {
    return null;
  }

  const handleNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (isValidNumberInput(value)) {
      setInputValue(value);
      debouncedHandleChange(value);
    }
  };

  const label = Object.prototype.hasOwnProperty.call(field, 'help') ? (
    <Fragment>
      {field.label}
      <ChartHelpTooltip chartType={field.help!} type="label" />
    </Fragment>
  ) : (
    field.label
  );

  return (
    <Grid size={field.size || 12} key={field.name}>
      <MuiTextField
        error={!!field.error}
        label={label}
        fullWidth
        required={field.required || false}
        disabled={Object.prototype.hasOwnProperty.call(field, 'disabled') ? !!getFormDataItem(formData, field.disabled!) : false}
        inputProps={{
          min: field.min || null,
          max: field.max || null,
        }}
        type="number"
        name={field.name}
        value={inputValue}
        onChange={handleNumberChange}
      />
    </Grid>
  );
};

export default NumberField;
